import { classifyHttpStatus } from './classify-http-status';
import { promoteCauseToTitle } from './promote-cause-to-title';

interface ISentryExceptionValue {
  type?: string;
  value?: string;
}

interface ISentryEventLike {
  exception?: { values?: ISentryExceptionValue[] };
  extra?: Record<string, unknown>;
  transaction?: string;
  fingerprint?: string[];
}

interface ISentryHintLike {
  originalException?: unknown;
}

interface IStructuredError {
  name: string;
  status?: number;
  path?: string;
  reason?: string;
  code?: string;
}

// Recognized structurally by `.name` (NOT `instanceof`) so @repo/config never imports
// @repo/sdk (the sdk already depends on config; importing back would be a cycle).
const RECOGNIZED_ERROR_NAMES = new Set([
  'APIResponseError',
  'APIClientError',
  'CloudflareError',
  'TimeoutError',
  'NetworkError',
]);

function readStructuredError(err: unknown): IStructuredError | null {
  if (typeof err !== 'object' || err === null) {
    return null;
  }
  const candidate = err as Record<string, unknown>;
  const name = typeof candidate.name === 'string' ? candidate.name : undefined;
  if (!name || !RECOGNIZED_ERROR_NAMES.has(name)) {
    return null;
  }
  return {
    name,
    status: typeof candidate.status === 'number' ? candidate.status : undefined,
    path: typeof candidate.path === 'string' ? candidate.path : undefined,
    reason: typeof candidate.reason === 'string' ? candidate.reason : undefined,
    code: typeof candidate.code === 'string' ? candidate.code : undefined,
  };
}

function resolveRoute(event: ISentryEventLike): string | undefined {
  if (typeof event.transaction === 'string' && event.transaction.length > 0) {
    return event.transaction;
  }
  const route = event.extra?.route;
  return typeof route === 'string' && route.length > 0 ? route : undefined;
}

function buildRouteSuffix(route: string | undefined, path: string | undefined): string {
  const target = route ?? path;
  return target ? ` · ${target}` : '';
}

function buildCloudflareDetail(err: IStructuredError): string {
  if (err.code) {
    return err.code;
  }
  return err.status !== undefined ? String(err.status) : 'unknown';
}

function buildTitle(err: IStructuredError, route: string | undefined): string {
  const suffix = buildRouteSuffix(route, err.path);
  if (err.name === 'CloudflareError') {
    return `Media vault unreachable (CF ${buildCloudflareDetail(err)}, ${err.reason ?? 'unknown'})${suffix}`;
  }
  if (err.name === 'TimeoutError') {
    return `Request timeout${suffix}`;
  }
  if (err.name === 'NetworkError') {
    return `Network error${suffix}`;
  }
  if (err.status !== undefined) {
    return `API ${err.status} ${classifyHttpStatus(err.status)}${suffix}`;
  }
  return `${err.name}${suffix}`;
}

export function normalizeErrorEvent<TEvent extends ISentryEventLike>(
  event: TEvent,
  hint?: ISentryHintLike,
): TEvent {
  // Keep the D1 title + grouping behavior intact first.
  promoteCauseToTitle(event, hint);

  const main = event.exception?.values?.at(-1);
  if (!main) {
    return event;
  }

  const err = readStructuredError(hint?.originalException);
  if (!err) {
    return event;
  }

  const route = resolveRoute(event);
  const title = buildTitle(err, route);
  const mutable = event as ISentryEventLike;

  if (main.value !== title) {
    const priorWrapped = event.extra?.wrappedError;
    mutable.extra = { ...event.extra, wrappedError: priorWrapped ?? main.value };
    main.value = title;
  }

  // Never clobber an explicit fingerprint (D1's ['d1-query-failed', ...] or the
  // media-vault ['media-vault-unreachable'] set at captureException time).
  if (!mutable.fingerprint) {
    mutable.fingerprint = [
      'api-error',
      err.name,
      err.status !== undefined ? String(err.status) : 'na',
      route ?? 'na',
    ];
  }

  return event;
}
